import { fork, put, take, cancel, call } from 'redux-saga/effects'
import { SnackbarType } from '../model/snackbar/type'
import { processStartAction, processStartEnd } from '../reducers/process/action'
import { snackbarShowAction } from '../reducers/snackbar/action'
import { OPERATION_SUCCESS, OPERATION_FAIL } from '../reducers/operation/type'
import SnackbarMessage from '../model/snackbar'
import { guid } from '../utils/guid'
import {
  remove as removeCookie,
  get as getCookie,
  set as setCookie,
} from 'es-cookie'
import { getCookieOptions } from '../utils/cookie-options'
import { refreshTokenService } from '../api/authentication'

// A 401 does not always mean the session is dead. Changing plan purges the old
// UserRole row (IdentityService.AssignRoleAsync with purgeSameCategoryRoles), so
// the RoleId claim baked into the access token points at a role that no longer
// exists and CurrentUserInfoQuery rejects it. That is what made a completed Stripe
// checkout land on /login. The refresh endpoint re-reads the role from the database
// and mints a token with the new RoleId, so one refresh recovers the session.
const REFRESH_ATTEMPT_KEY = 'authRefreshAttemptedAt'
const REFRESH_RETRY_COOLDOWN_MS = 30000

// Shared across concurrent callers. The app fires several requests at once
// (CurrentUserInfo alone goes out three times on load), so they all 401 together.
// Without this they race: the first starts a refresh and the rest fall straight
// through to logout, and navigating away cancels the in-flight refresh request.
let refreshInFlight = null

function tryRecoverSession() {
  // Already refreshing - join it rather than starting a second one or bailing.
  if (refreshInFlight) return refreshInFlight

  const refreshToken = getCookie('refreshToken')
  if (!refreshToken) return Promise.resolve(false)

  // One attempt per cooldown, persisted so it survives the reload below. A
  // refresh that keeps yielding a rejected access token logs the user out
  // instead of reload-looping forever.
  const lastAttempt = Number(sessionStorage.getItem(REFRESH_ATTEMPT_KEY) || 0)
  if (Date.now() - lastAttempt < REFRESH_RETRY_COOLDOWN_MS) {
    return Promise.resolve(false)
  }
  sessionStorage.setItem(REFRESH_ATTEMPT_KEY, String(Date.now()))

  refreshInFlight = refreshTokenService({ refreshToken })
    .then(response => {
      setCookie('token', response.data.accessToken, getCookieOptions())
      setCookie('refreshToken', response.data.refreshToken, getCookieOptions())
      setCookie('userId', response.data.userId, getCookieOptions())
      return true
    })
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null
    })

  return refreshInFlight
}
/**
 *
 * @param {*} callMethod
 * @param {*} payload
 */
export default function* generalSaga(callMethod, payload, preloading) {
  try {
    if (preloading == false) {
      // Preloader Hide if method for patch
    } else {
      yield put(processStartAction())
    }

    const task = yield fork(callMethod, payload)

    const resultAction = yield take([OPERATION_SUCCESS, OPERATION_FAIL])

    //let UnauthorizedTaskList = []

    if (resultAction.type === OPERATION_FAIL) {
      const { error } = resultAction

      const message = new SnackbarMessage(
        guid().uid(),
        SnackbarType.ERROR,
        null,
      )
      if (error.type === 'BadRequestError') {
        if (error._code == 'detail') {
          message.messageIds = [error.response]
          yield put(snackbarShowAction(message))
          return
        }
        message.messageIds = ['message.error.server']

        const { data } = error.response
        const { errors } = data
        if (errors) {
          const codes = []

          Object.keys(errors).forEach(function (key, index) {
            codes.push(errors[index])
          })

          message.messageIds = codes
        }
        yield put(snackbarShowAction(message))
      } else if (error.type === 'ServerError') {
        const { data } = error.response
        const { operationCode } = data

        message.messageIds = [operationCode]
        yield put(snackbarShowAction(message))
      } else if (error.type === 'ForbiddenError') {
        const { data } = error.response
        const { operationCode } = data
        message.messageIds = [operationCode]
        yield put(snackbarShowAction(message))
      } else if (error.type === 'UnauthorizedError') {
        const recovered = yield call(tryRecoverSession)

        if (recovered) {
          // Reload so the request that just 401'd is retried with the new token.
          window.location.reload()
        } else {
          message.messageIds = ['message.error.unauthorized']
          removeCookie('token')
          removeCookie('refreshToken')
          removeCookie('userId')
          window.location.href = '/'
        }
      } else if (error.response.status === 406) {
        const { data } = error.response
        const { operationCode } = data

        message.messageIds = [operationCode]
        yield put(snackbarShowAction(message))
      } else if (error.type === 'UnexpectedError') {
        message.messageIds = ['message.error.unexpected']
        yield put(snackbarShowAction(message))
      } else if (error.type === 'CustomError') {
        const { data } = error.response
        const { messages } = data
        const codes = []

        Object.keys(messages).forEach(function (key, index) {
          const message = messages[key]

          message.forEach(data => {
            codes.push(data.code)
          })
        })
        message.messageIds = codes
        yield put(snackbarShowAction(message))
      } else if (error.type === 'NetworkError') {
        message.messageIds = ['message.error.network']

        yield put(snackbarShowAction(message))
      }

      yield cancel(task)
    }
  } catch (err) {
    // error code
  } finally {
    if (preloading == false) {
      // Preloader Hide if method for patch
    } else {
      yield put(processStartEnd())
    }
  }
}
